import logger from "../../utils/logger";
import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Col,
  Container,
  Label,
  Row,
  Button,
  Form,
  Spinner,
  Input,
  InputGroup,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
} from "reactstrap";

// redux
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { authCheck, sendVerifyCode } from "../../slices/apps/authSlice";

// router
import { Link, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react select
import Select from "react-select";
const getSelectStyles = (validation, fieldName) => ({
  control: (base, state) => ({
    ...base,
    borderColor:
      validation.touched[fieldName] && validation.errors[fieldName]
        ? "#f06548" // Hata rengi
        : state.isFocused
        ? "#0ab39c" // Odaklanma rengi
        : "#ced4da",
    boxShadow: state.isFocused
      ? "0 0 0 0.2rem rgba(10, 179, 156, .25)"
      : base.boxShadow,
    "&:hover": {
      borderColor:
        validation.touched[fieldName] && validation.errors[fieldName]
          ? "#f06548"
          : "#0ab39c",
    },
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  }),
});

// react input mask
import { IMaskInput } from "react-imask";

// görsel importlar
import Logo from "../../assets/images/logo/metiner_avantaj_logo.png";

// data dosyaları
import CityJson from "../../common/il.json";

const cities = CityJson.map((data) => ({
  label: data.il_adi,
  value: data.il_adi,
  districts: data.ilceler
    .map((ilce) => ({
      label: ilce.ilce_adi,
      value: ilce.ilce_adi,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
})).sort((a, b) => a.label.localeCompare(b.label));

const companyTypeOptions = [
  { value: 0, label: "Şahıs Şirketi" },
  { value: 1, label: "Firma (LTD, A.Ş. vb.)" },
];

const RegisterCompany = () => {
  document.title = "İş Ortağımız Ol | Metiner Avantaj+";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [isPartnerScrolledToBottom, setIsPartnerScrolledToBottom] =
    useState(false);
  const partnerModalBodyRef = useRef(null);

  const [districts, setDistricts] = useState([]);

  const { registerCompanyLoading, authCheckLoading } = useSelector(
    (state) => ({
      registerCompanyLoading: state.auth.loading,
      authCheckLoading: state.auth.authCheckLoading,
    }),
    shallowEqual
  );

  const togglePartnerModal = () => {
    setPartnerModalOpen(!partnerModalOpen);
  };

  const checkPartnerScrollOnModalOpened = () => {
    if (partnerModalBodyRef.current) {
      const { scrollHeight, clientHeight } = partnerModalBodyRef.current;
      if (scrollHeight <= clientHeight) {
        setIsPartnerScrolledToBottom(true);
      }
    }
  };

  const resetPartnerScrollStateOnModalClosed = () => {
    setIsPartnerScrolledToBottom(false);
  };

  const handlePartnerScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
    if (bottom) {
      setIsPartnerScrolledToBottom(true);
    }
  };

  useEffect(() => {
    const storedAuthUserString = localStorage.getItem("authUser");
    if (!storedAuthUserString) return;

    const authCheckFunc = async () => {
      try {
        await dispatch(authCheck()).unwrap();
        navigate("/dashboard");
      } catch (error) {
        logger.error("Authentication check failed:", error);
      }
    };
    authCheckFunc();
  }, [dispatch, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: "",
      companyTableName: "",
      authName: "",
      authSurname: "",
      companyType: "",
      taxOffice: "",
      taxNumber: "",
      city: "",
      state: "",
      address: "",
      phoneNumber: "",
      email: "",
      partnerKvkkConsent: false,
    },
    validationSchema: Yup.object({
      companyName: Yup.string()
        .max(150, "Firma adı en fazla 150 karakter olabilir.")
        .required("Lütfen firma adını giriniz."),
      companyTableName: Yup.string()
        .max(150, "Tabela adı en fazla 150 karakter olabilir.")
        .required("Lütfen tabela adını giriniz."),
      authName: Yup.string()
        .max(50, "Yetkili adı en fazla 50 karakter olabilir.")
        .required("Lütfen yetkili adını giriniz."),
      authSurname: Yup.string()
        .max(50, "Yetkili soyadı en fazla 50 karakter olabilir.")
        .required("Lütfen yetkili soyadını giriniz."),
      companyType: Yup.number().required("Lütfen firma tipini seçiniz."),
      taxOffice: Yup.string()
        .max(100, "Vergi dairesi en fazla 100 karakter olabilir.")
        .required("Lütfen vergi dairesini giriniz."),
      taxNumber: Yup.string()
        .min(10, "TC/Vergi numarası en az 10 karakter olmalıdır.")
        .max(11, "TC/Vergi numarası en fazla 11 karakter olabilir.")
        .required("Lütfen Vergi / T.C. Kimlik numarasını giriniz."),
      city: Yup.string()
        .max(50, "İl en fazla 50 karakter olabilir.")
        .required("Lütfen il seçiniz."),
      state: Yup.string()
        .max(50, "İlçe en fazla 50 karakter olabilir.")
        .required("Lütfen ilçe seçiniz."),
      address: Yup.string()
        .max(250, "Adres en fazla 250 karakter olabilir.")
        .required("Lütfen adres giriniz."),
      phoneNumber: Yup.string()
        .max(20, "Telefon numarası en fazla 20 karakter olabilir.")
        .required("Lütfen telefon numarası giriniz."),
      email: Yup.string()
        .email("Lütfen geçerli bir e-posta adresi giriniz.")
        .max(100, "E-posta en fazla 100 karakter olabilir.")
        .required("Lütfen e-posta adresi giriniz."),
      partnerKvkkConsent: Yup.boolean().oneOf(
        [true],
        "Aracı Aydınlatma Metni'ni onaylamanız gerekmektedir."
      ),
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(
          sendVerifyCode({
            phoneNumber: values.phoneNumber,
            userType: 0,
            companyId: null,
          })
        ).unwrap();

        navigate("/verify-otp", { state: { ...values } });
      } catch (error) {
        logger.error("Firma kaydı başarısız:", error);
      }
    },
  });

  useEffect(() => {
    if (validation.values.city) {
      const selectedCity = cities.find(
        (c) => c.value === validation.values.city
      );
      setDistricts(selectedCity ? selectedCity.districts : []);

      if (validation.values.state) {
        validation.setFieldValue("state", "");
      }
    } else {
      setDistricts([]);
    }
  }, [validation.values.city]);

  const handlePartnerKvkkApproval = () => {
    validation.setFieldValue("partnerKvkkConsent", true);
    togglePartnerModal();
  };

  const handlePartnerCheckboxChange = () => {
    if (!validation.values.partnerKvkkConsent) {
      togglePartnerModal();
    } else {
      validation.setFieldValue("partnerKvkkConsent", false);
    }
  };

  if (authCheckLoading) {
    return (
      <div id="preloader">
        <div id="status">
          <img
            src={Logo}
            className="mb-2"
            alt="Metiner Avantaj+"
            style={{ objectFit: "contain", width: "10rem" }}
          />
          <div className="spinner-border text-primary avatar-xs" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="auth-page-wrapper d-flex justify-content-center align-items-center min-vh-100 py-5">
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center">
              <Col md={10}>
                <Card className="overflow-hidden">
                  <div className="text-center pt-5">
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={Logo} alt="Metiner Avantaj+" height="100" />
                    </Link>
                  </div>
                  <CardBody className="p-lg-5 p-4">
                    <div className="text-center mb-4">
                      <h4 className="mb-1">İş Ortağımız Olun</h4>
                    </div>
                    <Form onSubmit={validation.handleSubmit} autoComplete="off">
                      <Row className="gy-3">
                        <Col lg={6}>
                          <Label>Firma Adı</Label>
                          <Input
                            type="text"
                            name="companyName"
                            placeholder="Örnek A.Ş."
                            maxLength={150}
                            value={validation.values.companyName}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.companyName &&
                                validation.errors.companyName
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.companyName}
                          </FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Tabela Adı</Label>
                          <Input
                            type="text"
                            name="companyTableName"
                            placeholder="Örnek Manav"
                            maxLength={150}
                            value={validation.values.companyTableName}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.companyTableName &&
                                validation.errors.companyTableName
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.companyTableName}
                          </FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Firma Tipi</Label>
                          <Select
                            name="companyType"
                            placeholder="Seçiniz..."
                            options={companyTypeOptions}
                            menuPlacement="auto"
                            value={
                              companyTypeOptions.find(
                                (opt) =>
                                  opt.value === validation.values.companyType
                              ) || null
                            }
                            onChange={(option) =>
                              validation.setFieldValue(
                                "companyType",
                                option ? option.value : ""
                              )
                            }
                            styles={getSelectStyles(validation, "companyType")}
                          />
                          {validation.touched.companyType &&
                            validation.errors.companyType && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.companyType}
                              </div>
                            )}
                        </Col>

                        <div className="w-100 p-0 m-0"></div>

                        <Col lg={6}>
                          <Label>Yetkili Adı</Label>
                          <Input
                            type="text"
                            name="authName"
                            placeholder="Can"
                            maxLength={50}
                            value={validation.values.authName}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.authName &&
                                validation.errors.authName
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.authName}
                          </FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Yetkili Soyadı</Label>
                          <Input
                            type="text"
                            name="authSurname"
                            placeholder="Yılmaz"
                            maxLength={50}
                            value={validation.values.authSurname}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.authSurname &&
                                validation.errors.authSurname
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.authSurname}
                          </FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Vergi Dairesi</Label>
                          <Input
                            type="text"
                            name="taxOffice"
                            placeholder="Maslak Vergi Dairesi"
                            maxLength={100}
                            value={validation.values.taxOffice}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.taxOffice &&
                                validation.errors.taxOffice
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.taxOffice}
                          </FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Vergi / T.C. Kimlik Numarası</Label>
                          <IMaskInput
                            inputMode="numeric"
                            mask="0000000000[0]"
                            unmask={true}
                            name="taxNumber"
                            placeholder="11 haneli TC veya 10 haneli VKN"
                            value={validation.values.taxNumber}
                            onAccept={(value) => {
                              validation.setFieldValue("taxNumber", value);
                            }}
                            className={`form-control ${
                              validation.touched.taxNumber &&
                              validation.errors.taxNumber
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          {validation.touched.taxNumber &&
                            validation.errors.taxNumber && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.taxNumber}
                              </div>
                            )}
                        </Col>

                        <Col lg={6}>
                          <Label>E-posta Adresi</Label>
                          <Input
                            type="email"
                            name="email"
                            placeholder="firma@ornek.com"
                            maxLength={100}
                            value={validation.values.email}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                          />
                          <FormFeedback>{validation.errors.email}</FormFeedback>
                        </Col>
                        <Col lg={6}>
                          <Label>Telefon Numarası</Label>
                          <InputGroup>
                            <span
                              className="input-group-text"
                              style={{
                                width: "44px",
                                justifyContent: "center",
                              }}
                            >
                              0
                            </span>
                            <IMaskInput
                              inputMode="numeric"
                              mask="000 000 00 00"
                              unmask={true}
                              name="phoneNumber"
                              placeholder="5XX XXX XX XX"
                              value={validation.values.phoneNumber}
                              onAccept={(value) =>
                                validation.setFieldValue("phoneNumber", value)
                              }
                              className={`form-control ${
                                validation.touched.phoneNumber &&
                                validation.errors.phoneNumber
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ zIndex: 0 }}
                            />
                          </InputGroup>
                          {validation.touched.phoneNumber &&
                            validation.errors.phoneNumber && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.phoneNumber}
                              </div>
                            )}
                        </Col>

                        <Col lg={6}>
                          <Label>İl</Label>
                          <Select
                            name="city"
                            placeholder="İl Seçiniz"
                            isClearable
                            options={cities}
                            menuPlacement="auto"
                            value={
                              cities.find(
                                (item) => item.value === validation.values.city
                              ) || null
                            }
                            onChange={(event) =>
                              validation.setFieldValue(
                                "city",
                                event ? event.value : ""
                              )
                            }
                            styles={getSelectStyles(validation, "city")}
                          />
                          {validation.touched.city &&
                            validation.errors.city && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.city}
                              </div>
                            )}
                        </Col>
                        <Col lg={6}>
                          <Label>İlçe</Label>
                          <Select
                            name="state"
                            placeholder="İlçe Seçiniz"
                            isClearable
                            options={districts}
                            menuPlacement="auto"
                            value={
                              districts.find(
                                (item) => item.value === validation.values.state
                              ) || null
                            }
                            onChange={(event) =>
                              validation.setFieldValue(
                                "state",
                                event ? event.value : ""
                              )
                            }
                            styles={getSelectStyles(validation, "state")}
                            isDisabled={!validation.values.city}
                          />
                          {validation.touched.state &&
                            validation.errors.state && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.state}
                              </div>
                            )}
                        </Col>

                        <Col lg={12}>
                          <Label>Adres</Label>
                          <Input
                            type="textarea"
                            name="address"
                            rows="3"
                            placeholder="Firma adresi"
                            maxLength={250}
                            style={{ resize: "none" }}
                            value={validation.values.address}
                            onChange={validation.handleChange}
                            invalid={
                              !!(
                                validation.touched.address &&
                                validation.errors.address
                              )
                            }
                          />
                          <FormFeedback>
                            {validation.errors.address}
                          </FormFeedback>
                        </Col>

                        <Col lg={12}>
                          <div className="d-flex align-items-start">
                            <Input
                              type="checkbox"
                              id="partnerKvkkConsent"
                              name="partnerKvkkConsent"
                              className="me-2"
                              checked={validation.values.partnerKvkkConsent}
                              onChange={handlePartnerCheckboxChange}
                              style={{ cursor: "pointer" }}
                              invalid={
                                !!(
                                  validation.touched.partnerKvkkConsent &&
                                  validation.errors.partnerKvkkConsent
                                )
                              }
                            />
                            <Label
                              htmlFor="partnerKvkkConsent"
                              className="form-check-label mb-2"
                            >
                              <span
                                style={{
                                  cursor: "pointer",
                                  color: "#007bff",
                                  textDecoration: "underline",
                                }}
                              >
                                Aracı Aydınlatma Metni
                              </span>
                              'ni okudum, anladım ve onaylıyorum.
                            </Label>
                          </div>
                          {validation.touched.partnerKvkkConsent &&
                            validation.errors.partnerKvkkConsent && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.partnerKvkkConsent}
                              </div>
                            )}
                        </Col>

                        <Col lg={12} className="mt-4">
                          <Button
                            type="submit"
                            color="primary"
                            className="w-100"
                            disabled={
                              registerCompanyLoading ||
                              !validation.values.partnerKvkkConsent
                            }
                          >
                            {registerCompanyLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />{" "}
                                Kaydediliyor...
                              </>
                            ) : (
                              "İş Ortağı Ol"
                            )}
                          </Button>
                        </Col>
                      </Row>
                      <div className="mt-4 text-center">
                        <p className="mb-0">
                          Zaten bir hesabınız mı var?{" "}
                          <Link
                            to="/login"
                            className="fw-semibold text-primary text-decoration-underline"
                          >
                            Giriş Yap
                          </Link>
                        </p>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
        <footer className="footer h-auto pb-2">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted">
                  <p className="mb-2" style={{ letterSpacing: "0.8px" }}>
                    Metiner Avantaj+ &copy; {new Date().getFullYear()} | 0 352 3
                    555 444 | metiner@boschservice.com.tr
                    <br />
                    <i className="mdi mdi-heart text-danger"></i> by{" "}
                    <a
                      href="https://kodexis.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "#007bff" }}
                    >
                      Kodexis
                    </a>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>

      <Modal
        isOpen={partnerModalOpen}
        toggle={togglePartnerModal}
        centered
        size="lg"
        onOpened={checkPartnerScrollOnModalOpened}
        onClosed={resetPartnerScrollStateOnModalClosed}
      >
        <style>
          {`
            .modal-body-custom-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        <ModalHeader toggle={togglePartnerModal}>
          Aracı Aydınlatma Metni
        </ModalHeader>

        <ModalBody
          ref={partnerModalBodyRef}
          onScroll={handlePartnerScroll}
          className="modal-body-custom-scrollbar"
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <h6>Veri Sorumlusu</h6>
          <p>
            Metiner Avantaj+ Akaryakıt Madeni Yağ San. ve Tic. Ltd. Şti.
            ("Şirket")
          </p>

          <h6>Kapsam</h6>
          <p>
            Bu aydınlatma metni, Şirketimiz ile yapılan iş birliği kapsamında
            kuaför işletmelerinin kampanya sistemine kayıt olurken paylaştığı
            kişisel verilerin işlenmesine ilişkindir.
          </p>

          <h6>İşlenen Kişisel Veriler</h6>
          <ul>
            <li>İşletme adı, yetkili kişi adı-soyadı</li>
            <li>Telefon numarası, e-posta adresi</li>
            <li>İşletme adresi</li>
            <li>
              Kampanya katılım ve kazanç bilgileri (örneğin "kupon kullanımı
              başına kazanç tutarı")
            </li>
            <li>Fatura ve ödeme bilgileri</li>
          </ul>

          <h6>Veri İşleme Amaçları</h6>
          <ul>
            <li>İş birliği süreçlerinin yürütülmesi</li>
            <li>Kampanya gelir ve ödemelerinin hesaplanması</li>
            <li>
              İletişim, raporlama ve bilgilendirme süreçlerinin yönetilmesi
            </li>
            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
          </ul>

          <h6>Verilerin Aktarıldığı Taraflar</h6>
          <p>
            Kişisel verileriniz, kampanya sisteminin yönetimi amacıyla; ödeme
            altyapısı sağlayıcılarına, muhasebe ve raporlama hizmeti alınan
            firmalara ve gerektiğinde resmi kurumlara aktarılabilir.
          </p>

          <h6>Veri Toplama Yöntemi ve Hukuki Sebep</h6>
          <p>
            Kişisel verileriniz; iş birliği formu, sözleşme ve dijital sistem
            kayıtları aracılığıyla sözleşmenin kurulması ve ifası kapsamında
            toplanır. KVKK madde 5/2-(c), (ç), (e) ve (f) hükümlerine dayanır.
          </p>

          <h6>Kişisel Veri Sahibinin Hakları</h6>
          <p>
            KVKK madde 11 uyarınca; kişisel verilerinize ilişkin bilgi alma,
            düzeltilmesini veya silinmesini talep etme, işlenmeye itiraz etme
            gibi haklarınızı muhasebe@metiner.com.tr adresine e-posta göndererek
            kullanabilirsiniz.
          </p>
        </ModalBody>

        <ModalFooter className="justify-content-center mt-3">
          <Button
            color="primary"
            onClick={handlePartnerKvkkApproval}
            // disabled={!isPartnerScrolledToBottom}
          >
            Okudum, Onaylıyorum
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default RegisterCompany;
